import type {AgentProfile, ChatInputSlotProps, LeadingIconSlotProps} from '../agent-profile'
import {AGENT_INFO_REGISTRY} from '../agent-profile'
import {CliIcon} from '../../components/CliIcon'
import {CliPrivateIcon} from '../../components/CliPrivateIcon'
import {EmptyCliSession} from '../../components/session-logs/EmptyCliSession'
import {CLIChatInput} from '../../components/task-view/CLIChatInput'
import {BlockedTaskInput} from '../../components/task-view/BlockedTaskInput'
import {CLIModeToolbarSlot} from '../../components/task-view/CLIModeToolbarSlot'
import {computeConnectionStatus} from '../../utils/connection-status'
import {ConnectionStatus} from '../../types/session'
import type {SessionState} from '../../types/session'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {REMOTABLE_BASE} from './remotable-base'
import {useCurrentUser} from '../../contexts/CurrentUserContext'
import styles from '../../components/task-view/ChatInputDisplay.module.css'

function CLILeadingIcon({staleAt, dataUpdatedAt, state, sharingStatus}: LeadingIconSlotProps) {
  const connectionStatus = computeConnectionStatus(staleAt, true, state as SessionState, dataUpdatedAt)
  const isConnected =
    isFeatureEnabled('copilot_mission_control_cli_session_status') && connectionStatus === ConnectionStatus.Connected

  const isPrivate = isFeatureEnabled('copilot_mission_control_cli_private_icon') && sharingStatus !== 'shared'

  if (isPrivate) {
    return <CliPrivateIcon showConnectedDot={isConnected} />
  }

  return <CliIcon showConnectedDot={isConnected} />
}

function RemotableChatInputSlot({
  session,
  task,
  onCommandSent,
  cliRemoteNoticeDismissed,
  remoteControlPolicyEnabled,
}: ChatInputSlotProps) {
  const {currentUser, currentUserIsSessionOwner} = useCurrentUser()
  const remoteSteerable = task?.remote_steerable === true
  const agentTaskId = task?.agent_collaborators?.[0]?.agent_task_id
  const isShared = task?.sharing_status === 'shared'

  const isSessionOwner = currentUserIsSessionOwner || (!!currentUser && session.user_id === currentUser.id)

  if (!isSessionOwner && currentUser) {
    return (
      <div className={styles.inputWrapper}>
        <BlockedTaskInput>Only the session owner can steer this session</BlockedTaskInput>
      </div>
    )
  }

  return (
    <CLIChatInput
      session={session}
      remoteSteerable={remoteSteerable}
      remoteControlPolicyEnabled={remoteControlPolicyEnabled}
      agentTaskId={agentTaskId}
      staleAt={task?.stale_at}
      onCommandSent={onCommandSent}
      cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
      isShared={isShared}
      toolbarSlot={<CLIModeToolbarSlot sessionId={session.id} />}
    />
  )
}

/**
 * Profile overrides for the Copilot CLI agent.
 * Extends the remotable base with CLI-specific icon and identity.
 */
export const CLI_PROFILE: Partial<AgentProfile> = {
  ...REMOTABLE_BASE,
  slug: 'copilot-developer-cli',
  displayName: 'Copilot CLI',

  agentInfo: AGENT_INFO_REGISTRY['copilot-developer-cli'],

  slots: {
    LeadingIcon: CLILeadingIcon,
    ChatInput: RemotableChatInputSlot,
    EmptyState: EmptyCliSession,
  },
}
